import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, TrendingUp, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";

const SCHEMA = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "How to write LinkedIn posts that get 10x engagement (with AI)",
    "description": "Step-by-step guide to writing high-engagement LinkedIn posts using AI tools like Lakshmi. Covers hooks, storytelling, CTAs, and the 4-agent pipeline.",
    "url": "https://hilakshmi.com/blog/linkedin-posts-10x-engagement",
    "author": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "publisher": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "datePublished": "2026-01-15",
    "keywords": "LinkedIn post engagement, how to write LinkedIn posts, AI LinkedIn post writer, LinkedIn hooks India",
    "image": "https://hilakshmi.com/image/og-cover.svg"
};

const HOOK_TYPES = [
    { type: "The confession", example: "I posted 0 times on LinkedIn for 2 years. Here's what it cost me." },
    { type: "The number", example: "I got 47 job offers in 3 months without applying once. Here's how." },
    { type: "The counterintuitive take", example: "The best LinkedIn posts aren't about you. They're about your reader." },
    { type: "The question", example: "Why do some engineers become directors by 30 and others wait till 45?" },
];

export default function BlogPost1() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA);
        document.head.appendChild(s);
        document.title = "How to Write LinkedIn Posts That Get 10x Engagement — Lakshmi";
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", "Step-by-step guide to writing high-engagement LinkedIn posts with AI. Learn hooks, storytelling, CTAs, and why Indian professionals get better results with Lakshmi.");
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">

                {/* Hero */}
                <header className="mb-12">
                    <div className="lk-label mb-3">LinkedIn Growth · 8 min read</div>
                    <h1 className="font-heading text-3xl sm:text-5xl tracking-tight mb-6 leading-tight">
                        How to write LinkedIn posts that get <span className="lk-gradient-text">10x engagement</span> (with AI)
                    </h1>
                    <p className="text-[var(--lk-muted)] text-lg leading-relaxed mb-6">
                        Most LinkedIn posts get 5 likes from your college classmates and your mom. Here's why — and how to fix it in under 60 seconds.
                    </p>
                    {/* Visual stat bar */}
                    <div className="grid grid-cols-3 gap-3 lk-border lk-surface rounded-2xl p-5">
                        {[
                            { stat: "90%", label: "LinkedIn posts get <10 likes" },
                            { stat: "3x", label: "More reach with a strong hook" },
                            { stat: "60s", label: "With Lakshmi vs 60 min manually" },
                        ].map(s => (
                            <div key={s.stat} className="text-center">
                                <div className="font-bold text-xl sm:text-2xl lk-gradient-text">{s.stat}</div>
                                <div className="text-xs text-[var(--lk-muted)] mt-1">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </header>

                {/* Blog hero illustration */}
                <div className="mb-10 lk-border lk-surface rounded-2xl overflow-hidden">
                    <picture>
                        <source srcSet="/image/blog/linkedin-engagement.webp" type="image/webp" />
                        <img src="/image/blog/linkedin-engagement.png" alt="Indian professional growing LinkedIn engagement"
                            className="w-full h-48 sm:h-64 object-cover" loading="lazy"
                            onError={e => { e.target.style.display = "none"; }} />
                    </picture>
                </div>

                {/* Lakshmi character break */}
                <div className="flex items-center gap-6 my-10 lk-border lk-surface rounded-2xl p-6">
                    <picture>
                        <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                        <img src="/image/lakshmi_nobg_opt.png" alt="Lakshmi AI"
                            className="w-24 sm:w-32 shrink-0" loading="lazy"
                            style={{ filter: "drop-shadow(0 0 20px rgba(124,58,237,0.4))" }} />
                    </picture>
                    <div>
                        <div className="text-xs text-[var(--lk-primary)] font-semibold uppercase tracking-wider mb-1">TL;DR</div>
                        <p className="text-sm leading-relaxed">
                            The formula: <strong>scroll-stopping hook</strong> + <strong>relatable story</strong> + <strong>actionable insight</strong> + <strong>clear CTA</strong>. Lakshmi's 4-agent pipeline does all four — in under 60 seconds, in your voice.
                        </p>
                    </div>
                </div>

                {/* Section 1 */}
                <section className="mb-10">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-4">Why most LinkedIn posts fail</h2>
                    <p className="text-[var(--lk-muted)] leading-relaxed mb-4">
                        The LinkedIn algorithm shows your post to about 10% of your followers first. If those people engage in the first hour, it expands. If they scroll past, it dies. Everything rides on those first few lines — the hook.
                    </p>
                    <p className="text-[var(--lk-muted)] leading-relaxed mb-4">
                        Most people write like this: <em>"Excited to share that I've joined [Company] as [Title]! Looking forward to this new chapter…"</em>
                    </p>
                    <p className="leading-relaxed">
                        Nobody cares. Not because you're not interesting — but because this gives the reader no reason to click "see more."
                    </p>
                </section>

                {/* Section 2: Hook types — visual cards */}
                <section className="mb-10">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">The 4 hook types that stop the scroll</h2>
                    <div className="space-y-3">
                        {HOOK_TYPES.map((h, i) => (
                            <motion.div key={h.type}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.07 }}
                                viewport={{ once: true }}
                                className="lk-border lk-surface rounded-xl p-5"
                            >
                                <div className="text-xs font-semibold text-[var(--lk-primary)] uppercase tracking-wider mb-2">{h.type}</div>
                                <p className="text-sm italic">"{h.example}"</p>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* Section 3: The formula */}
                <section className="mb-10">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">The anatomy of a high-engagement LinkedIn post</h2>
                    {[
                        { n: 1, title: "Hook (lines 1–2)", desc: "One sentence that makes people click 'see more'. Counterintuitive, specific, or personal. Never generic." },
                        { n: 2, title: "The story (lines 3–8)", desc: "A real situation you were in. Tension, challenge, or mistake. Don't be inspirational — be honest." },
                        { n: 3, title: "The insight (lines 9–12)", desc: "What you actually learned. Specific, actionable, not 'be resilient'. Readers screenshot insights." },
                        { n: 4, title: "The CTA (last line)", desc: "Ask a question. 'What's yours?' or 'Drop your take below.' Simple CTAs dramatically increase comments." },
                    ].map(step => (
                        <div key={step.n} className="flex gap-5 mb-4 lk-border lk-surface rounded-xl p-5">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm"
                                style={{ background: "rgba(124,58,237,0.15)", color: "#9B5CF6" }}>
                                {step.n}
                            </div>
                            <div>
                                <div className="font-semibold mb-1">{step.title}</div>
                                <div className="text-sm text-[var(--lk-muted)]">{step.desc}</div>
                            </div>
                        </div>
                    ))}
                </section>

                {/* Section 4: How Lakshmi helps */}
                <section className="mb-12 lk-border rounded-2xl p-7"
                    style={{ background: "rgba(124,58,237,0.04)", borderColor: "rgba(124,58,237,0.3)" }}>
                    <div className="flex items-center gap-3 mb-5">
                        <Zap className="w-5 h-5 text-[var(--lk-primary)]" />
                        <h2 className="font-semibold text-lg">How Lakshmi handles all of this automatically</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                        {[
                            { agent: "Content Strategist", job: "Finds the angle in your idea. Picks the hook type that fits your story." },
                            { agent: "Voice Coach", job: "Calibrates tone to your LinkedIn voice profile. Sounds like you, not a bot." },
                            { agent: "Ghostwriter", job: "Drafts the full post — hook, story, insight, CTA. Platform-native format." },
                            { agent: "QA Editor", job: "Scores the post 0–100. Retries automatically if the hook is weak." },
                        ].map(a => (
                            <div key={a.agent} className="lk-border lk-surface rounded-xl p-4">
                                <div className="font-medium text-[var(--lk-primary)] mb-1">{a.agent}</div>
                                <div className="text-[var(--lk-muted)]">{a.job}</div>
                            </div>
                        ))}
                    </div>
                    <p className="text-sm text-[var(--lk-muted)] mt-5">Total time: under 60 seconds. No blank screen. No editing loop.</p>
                </section>

                {/* CTA */}
                <div className="text-center lk-border lk-surface rounded-2xl p-8">
                    <div className="flex justify-center mb-4">
                        <TrendingUp className="w-8 h-8 text-[var(--lk-primary)]" />
                    </div>
                    <h3 className="font-heading text-2xl tracking-tight mb-3">Ready to write your first high-engagement post?</h3>
                    <p className="text-[var(--lk-muted)] text-sm mb-6">Lakshmi is free. No credit card. Drop a rough idea and get a scored, publish-ready LinkedIn post in under a minute.</p>
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                        Write my first post — free <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </article>
        </div>
    );
}
